// frontend/src/components/chat/CreateGroupModal.jsx
// PHASE 6: Modal for creating new group conversations
// Allows selecting multiple participants and setting group name/description

import React, { useState, useEffect, useCallback } from "react";
import { useChat } from "../../contexts/ChatContext";

const CreateGroupModal = ({ onClose, onGroupCreated }) => {
  const { fetchChatableUsers, chatableUsers, createGroup, isUserOnline } =
    useChat();

  // Form state
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // UI state
  const [isSearching, setIsSearching] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1 = select users, 2 = set group info

  // Fetch users on mount and when search changes
  useEffect(() => {
    const searchUsers = async () => {
      setIsSearching(true);
      await fetchChatableUsers(searchQuery);
      setIsSearching(false);
    };

    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, fetchChatableUsers]);

  // Toggle user selection
  const toggleUserSelection = useCallback((user) => {
    setSelectedUsers((prev) => {
      const isSelected = prev.some((u) => u.user_id === user.user_id);
      if (isSelected) {
        return prev.filter((u) => u.user_id !== user.user_id);
      } else {
        return [...prev, user];
      }
    });
  }, []);

  // Remove selected user
  const removeSelectedUser = useCallback((userId) => {
    setSelectedUsers((prev) => prev.filter((u) => u.user_id !== userId));
  }, []);

  // Check if user is selected
  const isUserSelected = useCallback(
    (userId) => {
      return selectedUsers.some((u) => u.user_id === userId);
    },
    [selectedUsers]
  );

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get role badge color
  const getRoleBadgeColor = (role) => {
    const r = role?.toLowerCase();
    if (r === "admin") return "danger";
    if (r === "supervisor" || r === "manager") return "warning";
    if (r === "job_coach") return "info";
    return "secondary";
  };

  // Handle next step
  const handleNextStep = () => {
    if (selectedUsers.length === 0) {
      setError("Please select at least one participant");
      return;
    }
    setError(null);
    setStep(2);
  };

  // Handle back to step 1
  const handleBack = () => {
    setStep(1);
    setError(null);
  };

  // Handle create group
  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      setError("Please enter a group name");
      return;
    }

    if (selectedUsers.length === 0) {
      setError("Please select at least one participant");
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const participantIds = selectedUsers.map((u) => u.user_id);
      const group = await createGroup(groupName.trim(), participantIds, {
        description: groupDescription.trim() || null,
      });

      onGroupCreated(group);
    } catch (err) {
      console.error("Failed to create group:", err);
      setError(
        err.response?.data?.error || "Failed to create group. Please try again."
      );
    } finally {
      setIsCreating(false);
    }
  };

  // Handle key press
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && step === 2 && !isCreating && groupName.trim()) {
      handleCreateGroup();
    }
    if (e.key === "Escape") {
      onClose();
    }
  };

  return (
    <div className="create-group-overlay" onClick={onClose}>
      <div
        className="create-group-modal"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="create-group-header">
          <h5 className="m-0">
            {step === 1 ? "Select Participants" : "Create Group"}
          </h5>
          <button className="btn btn-link text-muted p-0" onClick={onClose}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {/* Error display */}
        {error && (
          <div className="alert alert-danger m-3 mb-0 py-2">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {/* Step 1: Select Participants */}
        {step === 1 && (
          <>
            {/* Selected users display */}
            {selectedUsers.length > 0 && (
              <div className="selected-users-container">
                <div className="selected-users-scroll">
                  {selectedUsers.map((user) => (
                    <div key={user.user_id} className="selected-user-chip">
                      <span className="chip-name">{user.name}</span>
                      <button
                        className="chip-remove"
                        onClick={() => removeSelectedUser(user.user_id)}
                        title="Remove"
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                  ))}
                </div>
                <div className="selected-count">
                  {selectedUsers.length} selected
                </div>
              </div>
            )}

            {/* Search input */}
            <div className="create-group-search">
              <div className="chat-search-box">
                <i className="bi bi-search"></i>
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="chat-search-input"
                  autoFocus
                />
                {searchQuery && (
                  <button
                    className="chat-search-clear"
                    onClick={() => setSearchQuery("")}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>
            </div>

            {/* User list */}
            <div className="create-group-user-list">
              {isSearching ? (
                <div className="chat-loading py-4">
                  <div
                    className="spinner-border spinner-border-sm text-primary"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="ms-2">Searching users...</span>
                </div>
              ) : chatableUsers.length === 0 ? (
                <div className="chat-empty py-4 text-center text-muted">
                  <i className="bi bi-people d-block fs-3 mb-2"></i>
                  {searchQuery
                    ? "No users found matching your search"
                    : "No users available"}
                </div>
              ) : (
                chatableUsers.map((user) => {
                  const selected = isUserSelected(user.user_id);
                  const online = isUserOnline(user.user_id);

                  return (
                    <div
                      key={user.user_id}
                      className={`user-select-item ${
                        selected ? "selected" : ""
                      }`}
                      onClick={() => toggleUserSelection(user)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          toggleUserSelection(user);
                        }
                      }}
                    >
                      <div className="user-select-checkbox">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleUserSelection(user)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="avatar-with-status">
                        <div className="chat-avatar small">
                          {getInitials(user.name)}
                        </div>
                        {online && <span className="online-dot"></span>}
                      </div>
                      <div className="user-select-info">
                        <div className="user-select-name">{user.name}</div>
                        <div className="user-select-details">
                          <span
                            className={`badge bg-${getRoleBadgeColor(
                              user.role
                            )} badge-sm`}
                          >
                            {user.role}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="create-group-footer">
              <button className="btn btn-outline-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleNextStep}
                disabled={selectedUsers.length === 0}
              >
                Next
                <i className="bi bi-arrow-right ms-1"></i>
              </button>
            </div>
          </>
        )}

        {/* Step 2: Group Info */}
        {step === 2 && (
          <>
            <div className="create-group-form">
              {/* Group avatar preview */}
              <div className="group-avatar-preview">
                <div className="chat-avatar large group-avatar">
                  <i className="bi bi-people-fill"></i>
                </div>
                <div className="group-preview-info">
                  <div className="group-preview-name">
                    {groupName || "Group Name"}
                  </div>
                  <div className="group-preview-members">
                    {selectedUsers.length + 1} members
                  </div>
                </div>
              </div>

              {/* Group name input */}
              <div className="form-group">
                <label htmlFor="groupName" className="form-label">
                  Group Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="groupName"
                  className="form-control"
                  placeholder="Enter group name"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  maxLength={100}
                  autoFocus
                />
                <div className="form-text">
                  {groupName.length}/100 characters
                </div>
              </div>

              {/* Group description input */}
              <div className="form-group">
                <label htmlFor="groupDescription" className="form-label">
                  Description (optional)
                </label>
                <textarea
                  id="groupDescription"
                  className="form-control"
                  placeholder="What's this group about?"
                  value={groupDescription}
                  onChange={(e) => setGroupDescription(e.target.value)}
                  rows={3}
                  maxLength={500}
                />
                <div className="form-text">
                  {groupDescription.length}/500 characters
                </div>
              </div>

              {/* Participants preview */}
              <div className="form-group">
                <label className="form-label">
                  Participants ({selectedUsers.length})
                </label>
                <div className="participants-preview">
                  {selectedUsers.slice(0, 5).map((user) => (
                    <div key={user.user_id} className="participant-chip">
                      <div className="chat-avatar tiny">
                        {getInitials(user.name)}
                      </div>
                      <span>{user.name.split(" ")[0]}</span>
                    </div>
                  ))}
                  {selectedUsers.length > 5 && (
                    <div className="participant-chip more">
                      +{selectedUsers.length - 5} more
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="create-group-footer">
              <button
                className="btn btn-outline-secondary"
                onClick={handleBack}
                disabled={isCreating}
              >
                <i className="bi bi-arrow-left me-1"></i>
                Back
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateGroup}
                disabled={isCreating || !groupName.trim()}
              >
                {isCreating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1"></span>
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-people-fill me-1"></i>
                    Create Group
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CreateGroupModal;
